import { Layout, PlotData } from 'plotly.js';
import { ShakerControlSequence } from 'src/app/model/shaker-control/shaker-control-sequence';
import { ShakerFeatureDescriptor } from 'src/app/model/shaker-control/shaker-feature-descriptor';
import { determineYAxesSpacingFactor } from '../layout-processor/y-spacing-factor';
import { baseDateToOffset, getAxisLongFromShortName } from '../plotly';
import { hovertemplateBase } from '../plotly/hovertemplate-globals';
import { tickformatstopsBase } from '../plotly/tickformatstops';


type FeatureToAxisMapping = { [featureId: string]: string };

/**
 * Turn a shaker control sequence into a plot data representation format
 * usable by Plotly.
 *
 * @param input Object containing the sequence and feature descriptors
 * @param mapping Mapping of feature IDs to y-axis shorthands (y, y2, y3,...)
 * @returns
 */
export function convertShakerSequenceToPlotData(
  input: {
    sequence: ShakerControlSequence;
    featureDescriptors: ShakerFeatureDescriptor[];
  },
  mapping: FeatureToAxisMapping
): Partial<PlotData>[] {
  type TimeEntry = {
    time: number;
    types: {
      // Value for the given types at the time
      [type: string]: any;
    };
  };

  const { sequence, featureDescriptors } = input;

  // Group entries by type and time
  const typeValuesByTime: TimeEntry[] = [];

  // Features seen while traversing the sequence list
  const featureTypes: string[] = [];

  let holdingTime = 0;
  let timeEntry: TimeEntry = {
    time: holdingTime,
    types: {}
  };
  typeValuesByTime.push(timeEntry);

  sequence.forEach(entry => {
    if (entry.type === undefined || entry.value === undefined) {
      return;
    }

    if (entry.type === 'TIME') {
      holdingTime += entry.value;
      timeEntry = {
        time: holdingTime,
        types: {}
      };
      typeValuesByTime.push(timeEntry);
    } else {
      if (featureTypes.indexOf(entry.type) === -1) {
        featureTypes.push(entry.type);
      }

      timeEntry.types[entry.type] = entry.value;
    }
  });


  // If in a time-entry a feature of the feature list is not present,
  // add the previous value of that feature.
  featureTypes.forEach(feature => {
    typeValuesByTime.forEach((timeEntry, idx) => {
      if (timeEntry.types[feature] === undefined) {
        const lastTimeEntry = typeValuesByTime[idx - 1];
        if (lastTimeEntry !== undefined) {
          timeEntry.types[feature] = lastTimeEntry.types[feature];
        }
      }
    });
  });

  // Group time entries by type and create plot data

  const result = featureTypes.map<Partial<PlotData>>(type => {
    const fd = featureDescriptors.find(
      descriptor => descriptor.id === type
    );

    if (fd === undefined) {
      throw new Error(`No descriptor found for feature ${type}`);
    }

    const color = fd.colorHex || '#000000';
    const yaxis = mapping[type];
    if (yaxis === undefined) {
      throw new Error(`No y-axis mapping found for feature ${type}`);
    }


    const timeZoneOffset = baseDateToOffset(new Date(0).toISOString());
    return {
      // Probably not worth it to trigger initialization of a webgl context
      // because of the probably limited amount of data points
      type: 'scatter',
      x: typeValuesByTime.map(entry => new Date((entry.time * 1000) - timeZoneOffset)),
      y: typeValuesByTime.map(entry => entry.types[type]),
      name: fd.name,
      line: { color, shape: 'hv' },
      marker: { color },
      hovertemplate: hovertemplateBase,
      xaxis: 'x', // x is always the time-axis,
      yaxis
    };
  });

  return result;
}


export function getShakerSequencePlotLayoutBase() {
  const layout: Partial<Layout> = {
    autosize: true,
    margin: {
      l: 60,
      r: 0,
      b: 70,
      t: 60,
    },
    height: 300,
    // There should only be one axis - the time-axis (x)
    xaxis: {
      type: 'date',
      title: 'Time',
      tickformatstops: tickformatstopsBase,
    }
  };

  return layout;
}


/**
 *
 * @param input Object containing the sequence and feature descriptors.
 *           Each feature used has to have a descriptor provided.
 * @returns The plotly layout with y-axes added and a mapping of feature IDs to y-axis shorthands.
 */
export function getShakerSequencePlotLayoutAndFeatureToAxisMapping(
  input: {
    sequence: ShakerControlSequence;
    featureDescriptors: ShakerFeatureDescriptor[];
  },
  viewSize?: { width: number }
): { layout: Partial<Layout>; mapping: FeatureToAxisMapping } {
  // Group sequences by unit
  const unitGroups = generateUnitGroups(input);

  // transform unitGroups to a map of schema `featureId => yAxisShort`
  const featureToYAxisShort: FeatureToAxisMapping = {};
  for (const [, group] of Object.entries(unitGroups)) {
    group.featureIds.forEach(featureId => {
      featureToYAxisShort[featureId] = group.yAxisShort;
    });
  }

  const layout = getShakerSequencePlotLayoutBase();

  // Add required y-axes to the layout

  // Sort the unit keys to ensure a consistent order from y to y2 to y3, etc.
  const unitKeys = Object.keys(unitGroups).sort();

  let spacingFactor = 0.05;
  if (viewSize) {
    spacingFactor = determineYAxesSpacingFactor(viewSize);
  }

  for (let i = 0; i < unitKeys.length; i++) {
    const yAxisShort = unitGroups[unitKeys[i]].yAxisShort;
    const yAxisLong  = getAxisLongFromShortName(yAxisShort) as keyof Partial<Layout>;
    const colors     = unitGroups[unitKeys[i]].colorHexes;

    const unit  = unitKeys[i];
    const title = unit + ' ' + colors.map(c => `<span style="color:${c}">&#9679;</span>`);

    (layout as any)[yAxisLong] = {
      overlaying: yAxisShort !== 'y' ? 'y' : undefined,
      side: 'left',
      anchor: 'free',
      position: i * spacingFactor,
      title: {
        text: title,
        standoff: 0
      }
    } as Partial<Layout['yaxis']>;
  }

  // Adjust domains of plot, so the y-axes don't intersect with it
  layout.xaxis = { ...layout.xaxis, domain: [unitKeys.length * spacingFactor, 1] };

  return { layout, mapping: featureToYAxisShort };
}


/**
 * Intermediate function to generate unit groups from a sequence.
 *
 * Used by {@link getShakerControlSequencePlotLayout}.
 *
 * @param input
 * @returns List of unit groups, where each unit group contains a list of feature IDs
 *   that are part of the grpup, the y-axis shorthand and the colors of the features.
 */
function generateUnitGroups(
  input: {
    sequence: ShakerControlSequence;
    featureDescriptors: ShakerFeatureDescriptor[];
  }
) {
  const { sequence, featureDescriptors: descriptors } = input;

  const unitGroups: {
    [unit: string]: { featureIds: string[]; yAxisShort: string; colorHexes: string[] };
  } = {};

  // Just so we don't have to iterate over the descriptors multiple times
  const seenTypes: string[] = [];

  // Used to determine the y-axis shorthand: 1=y, 2=y2, 3=y3, etc.
  let YAxisCounter = 1;

  sequence.forEach(item => {
    if (seenTypes.indexOf(item.type) !== -1) {
      return;
    }

    if (item.type === 'TIME' || item.type === undefined) {
      return;
    }

    seenTypes.push(item.type);

    const descriptor = descriptors.find(
      descriptor => descriptor.id === item.type
    );
    if (descriptor === undefined) {
      throw new Error(`No descriptor found for feature ${item.type}`);
    }

    const unit = descriptor.unit;

    let unitGroup = unitGroups[unit];
    if (!unitGroup) {
      const yAxisShort = 'y' + (YAxisCounter > 1 ? YAxisCounter : '');
      ++YAxisCounter;

      unitGroup = {
        yAxisShort,
        featureIds: [item.type],
        colorHexes: [descriptor.colorHex]
      };

      unitGroups[unit] = unitGroup;
    } else {
      unitGroup.featureIds.push(item.type);
      unitGroup.colorHexes.push(descriptor.colorHex);
    }
  });

  return unitGroups;
}

